/**
 * Validazione/normalizzazione client-side degli item delle sezioni ripetibili
 * del CV Europass, usata dall'Editable_Mode della vista condivisa
 * (`EuropassCvViewer` / `ExternalCvProfileView`).
 *
 * Perché qui e non riusando direttamente gli schema `zod` dell'API: gli schema
 * di sezione (`workExperienceSchema`, `educationSchema`, ecc.) vivono in
 * `api/src/domain/europassCv.ts`, non sono esportati singolarmente e `zod` non
 * è una dipendenza del frontend. Questo modulo replica *lo stesso contratto* di
 * quegli schema — tutti i campi opzionali, stringhe "morbide" (trim → assente
 * se vuote), booleani morbidi, array di stringhe compattati — restando
 * allineato al validatore server-side che resta l'autorità finale alla
 * persistenza (`europassCvSchema` + `pruneEuropassCv`).
 *
 * Ogni validatore è una funzione pura `(input) => { data } | { errors }` che
 * normalizza l'item e segnala i problemi di validazione per il feedback inline
 * nel form. Dato che gli schema Europass hanno tutti i campi opzionali, la
 * validazione reale si limita alla coercizione/pulizia e al requisito minimo
 * che l'item non sia completamente vuoto (una voce senza alcun campo non è
 * utile né mostrabile dalle sezioni, che filtrano gli item vuoti).
 */
import type {
  EuropassAward,
  EuropassEducation,
  EuropassLanguageSkill,
  EuropassPersonalInfo,
  EuropassProject,
  EuropassPublication,
  EuropassSkills,
  EuropassWorkExperience,
} from '../shared/dto';

/** Sezioni ripetibili del CV editabili in Editable_Mode. */
export type RepeatableSection =
  | 'workExperience'
  | 'education'
  | 'languages'
  | 'projects'
  | 'awards'
  | 'publications';

/** Esito di una validazione: item normalizzato oppure messaggi di errore. */
export type SectionValidationResult<T> =
  | { success: true; data: T }
  | { success: false; errors: string[] };

/**
 * Coerce "morbido" a stringa (allineato a `coerceString` dell'API): trim e
 * normalizzazione a `undefined` per valori nulli/vuoti.
 */
function softString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** Coerce "morbido" a booleano: `undefined` quando non esplicitamente booleano. */
function softBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

/**
 * Coerce "morbido" ad array di stringhe non vuote: scarta i frammenti vuoti e
 * restituisce `undefined` se non resta nulla (come `softStringArray` dell'API).
 */
function softStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return softString(value) ? [softString(value) as string] : undefined;
  const cleaned = value
    .map((v) => softString(v))
    .filter((v): v is string => v !== undefined);
  return cleaned.length > 0 ? cleaned : undefined;
}

/**
 * Rimuove le chiavi `undefined` da un oggetto item, così da persistere solo i
 * campi valorizzati (coerente con `pruneEuropassCv`). Restituisce `undefined`
 * se l'oggetto risultante è vuoto.
 */
function compact<T>(item: Record<string, unknown>): T | undefined {
  const entries = Object.entries(item).filter(([, v]) => v !== undefined);
  return entries.length > 0 ? (Object.fromEntries(entries) as T) : undefined;
}

/** Etichetta leggibile della sezione (per i messaggi d'errore). */
export const SECTION_LABELS: Record<RepeatableSection, string> = {
  workExperience: 'esperienza lavorativa',
  education: 'istruzione e formazione',
  languages: 'lingua',
  projects: 'progetto',
  awards: 'riconoscimento',
  publications: 'pubblicazione',
};

function validateWorkExperience(
  input: Record<string, unknown>,
): SectionValidationResult<EuropassWorkExperience> {
  const data = compact<EuropassWorkExperience>({
    jobTitle: softString(input.jobTitle),
    employer: softString(input.employer),
    city: softString(input.city),
    country: softString(input.country),
    startDate: softString(input.startDate),
    endDate: softString(input.endDate),
    current: softBoolean(input.current),
    description: softString(input.description),
    sector: softString(input.sector),
  });
  if (data === undefined || (data.jobTitle === undefined && data.employer === undefined)) {
    return {
      success: false,
      errors: ['Indica almeno il ruolo o il datore di lavoro.'],
    };
  }
  return { success: true, data };
}

function validateEducation(
  input: Record<string, unknown>,
): SectionValidationResult<EuropassEducation> {
  const data = compact<EuropassEducation>({
    qualification: softString(input.qualification),
    institution: softString(input.institution),
    city: softString(input.city),
    country: softString(input.country),
    startDate: softString(input.startDate),
    endDate: softString(input.endDate),
    current: softBoolean(input.current),
    description: softString(input.description),
    level: softString(input.level),
  });
  if (
    data === undefined ||
    (data.qualification === undefined && data.institution === undefined)
  ) {
    return {
      success: false,
      errors: ['Indica almeno il titolo o l\u2019istituto.'],
    };
  }
  return { success: true, data };
}

function validateLanguage(
  input: Record<string, unknown>,
): SectionValidationResult<EuropassLanguageSkill> {
  const data = compact<EuropassLanguageSkill>({
    language: softString(input.language),
    mother: softBoolean(input.mother),
    listening: softString(input.listening),
    reading: softString(input.reading),
    spokenInteraction: softString(input.spokenInteraction),
    spokenProduction: softString(input.spokenProduction),
    writing: softString(input.writing),
    overall: softString(input.overall),
  });
  if (data === undefined || data.language === undefined) {
    return { success: false, errors: ['Indica la lingua.'] };
  }
  return { success: true, data };
}

function validateProject(
  input: Record<string, unknown>,
): SectionValidationResult<EuropassProject> {
  const data = compact<EuropassProject>({
    name: softString(input.name),
    description: softString(input.description),
    startDate: softString(input.startDate),
    endDate: softString(input.endDate),
    technologies: softStringArray(input.technologies),
  });
  if (data === undefined || data.name === undefined) {
    return { success: false, errors: ['Indica il nome del progetto.'] };
  }
  return { success: true, data };
}

function validateAward(
  input: Record<string, unknown>,
): SectionValidationResult<EuropassAward> {
  const data = compact<EuropassAward>({
    title: softString(input.title),
    issuer: softString(input.issuer),
    date: softString(input.date),
    description: softString(input.description),
  });
  if (data === undefined || data.title === undefined) {
    return { success: false, errors: ['Indica il titolo del riconoscimento.'] };
  }
  return { success: true, data };
}

function validatePublication(
  input: Record<string, unknown>,
): SectionValidationResult<EuropassPublication> {
  const data = compact<EuropassPublication>({
    title: softString(input.title),
    publisher: softString(input.publisher),
    date: softString(input.date),
    reference: softString(input.reference),
  });
  if (data === undefined || data.title === undefined) {
    return { success: false, errors: ['Indica il titolo della pubblicazione.'] };
  }
  return { success: true, data };
}

// ---------------------------------------------------------------------------
// Sezioni singole (non ripetibili): dati personali, profilo, competenze
// ---------------------------------------------------------------------------

/** Sezioni singole del CV editabili tramite form dedicato in Editable_Mode. */
export type SingleSection = 'personalInfo' | 'summary' | 'skills';

/**
 * Coerce "morbido" ad array di stringhe a partire da un testo multilinea o
 * separato da virgole: una riga/elemento per link. Scarta i frammenti vuoti e
 * restituisce `undefined` se non resta nulla.
 */
function softLinks(value: unknown): string[] | undefined {
  if (Array.isArray(value)) return softStringArray(value);
  if (typeof value !== 'string') return undefined;
  const cleaned = value
    .split(/[\n,]/)
    .map((v) => v.trim())
    .filter((v) => v !== '');
  return cleaned.length > 0 ? cleaned : undefined;
}

/**
 * Valida/normalizza i dati personali (sezione singola). Tutti i campi sono
 * opzionali; l'indirizzo è a sua volta compattato. L'item è accettato anche se
 * parzialmente vuoto: una sezione dati personali vuota è semplicemente omessa
 * dalla persistenza (coerente con `pruneEuropassCv`).
 */
function validatePersonalInfo(
  input: Record<string, unknown>,
): SectionValidationResult<EuropassPersonalInfo> {
  const address = compact<Record<string, unknown>>({
    street: softString(input.street),
    city: softString(input.city),
    postalCode: softString(input.postalCode),
    region: softString(input.region),
    country: softString(input.country),
  });
  const data = compact<EuropassPersonalInfo>({
    firstName: softString(input.firstName),
    lastName: softString(input.lastName),
    headline: softString(input.headline),
    email: softString(input.email),
    phone: softString(input.phone),
    ...(address !== undefined ? { address: address as EuropassPersonalInfo['address'] } : {}),
    dateOfBirth: softString(input.dateOfBirth),
    nationality: softString(input.nationality),
    gender: softString(input.gender),
    links: softLinks(input.links),
  });
  // Sezione completamente vuota ⇒ oggetto vuoto (sarà rimosso dal pruning lato
  // API); nessun campo è obbligatorio.
  return { success: true, data: data ?? {} };
}

/**
 * Valida/normalizza il profilo professionale (sezione `summary`, testo libero).
 * Restituisce `{ summary }` con la stringa normalizzata, oppure stringa vuota
 * (il pruning lato API rimuove un summary vuoto).
 */
function validateSummary(
  input: Record<string, unknown>,
): SectionValidationResult<{ summary: string }> {
  const summary = softString(input.summary);
  return { success: true, data: { summary: summary ?? '' } };
}

/**
 * Valida/normalizza le competenze testuali (sezione singola `skills`). Tutti i
 * campi sono opzionali e in testo libero.
 */
function validateSkills(
  input: Record<string, unknown>,
): SectionValidationResult<EuropassSkills> {
  const data = compact<EuropassSkills>({
    communication: softString(input.communication),
    organisational: softString(input.organisational),
    jobRelated: softString(input.jobRelated),
    digital: softString(input.digital),
    other: softString(input.other),
  });
  return { success: true, data: data ?? {} };
}

/**
 * Valida/normalizza l'input grezzo di una sezione singola, restituendo il
 * valore da inviare come `data` a `onSectionUpdate(section, data)`.
 */
export function validateSingleSection(
  section: SingleSection,
  input: Record<string, unknown>,
): SectionValidationResult<unknown> {
  switch (section) {
    case 'personalInfo':
      return validatePersonalInfo(input);
    case 'summary':
      return validateSummary(input);
    case 'skills':
      return validateSkills(input);
    default: {
      const exhaustive: never = section;
      throw new Error(`Sezione singola non gestita: ${String(exhaustive)}`);
    }
  }
}

/** Tipo item per sezione ripetibile. */
export interface SectionItemByName {
  workExperience: EuropassWorkExperience;
  education: EuropassEducation;
  languages: EuropassLanguageSkill;
  projects: EuropassProject;
  awards: EuropassAward;
  publications: EuropassPublication;
}

/**
 * Valida/normalizza un item grezzo (dai campi del form) per la sezione data,
 * rispecchiando lo schema zod di sezione dell'API. Non solleva: restituisce
 * sempre un esito strutturato per il feedback inline.
 */
export function validateSectionItem<S extends RepeatableSection>(
  section: S,
  input: Record<string, unknown>,
): SectionValidationResult<SectionItemByName[S]> {
  switch (section) {
    case 'workExperience':
      return validateWorkExperience(input) as SectionValidationResult<SectionItemByName[S]>;
    case 'education':
      return validateEducation(input) as SectionValidationResult<SectionItemByName[S]>;
    case 'languages':
      return validateLanguage(input) as SectionValidationResult<SectionItemByName[S]>;
    case 'projects':
      return validateProject(input) as SectionValidationResult<SectionItemByName[S]>;
    case 'awards':
      return validateAward(input) as SectionValidationResult<SectionItemByName[S]>;
    case 'publications':
      return validatePublication(input) as SectionValidationResult<SectionItemByName[S]>;
    default: {
      // Esaustività: ogni sezione ripetibile deve avere un validatore. Se si
      // aggiunge una sezione senza aggiornare lo switch, TypeScript segnala qui.
      const exhaustive: never = section;
      throw new Error(`Sezione non gestita: ${String(exhaustive)}`);
    }
  }
}
